<template>
  <section class="article-media-module-component">
    <div class="sa-container">
      <figure
        class="media-content"
        :class="data.full_width ? 'full-width' : 'small'">
        <div
          v-if="isVideo"
          class="media-container video-container">
          <transition name="overlay-fade">
            <button
              v-if="hasVideo && !videoStarted"
              class="play-overlay"
              type="button"
              aria-label="Play video"
              @click="startPlayback">
              <img
                v-if="posterImageUrl"
                class="overlay-poster"
                :src="posterImageUrl"
                :alt="posterImageAlt" />

              <img
                class="play-icon"
                src="@/assets/img/ras_play_button.png"
                alt="" />
            </button>
          </transition>

          <iframe
            v-if="isVimeo && vimeoEmbedUrl"
            class="media-player video-player"
            :src="vimeoEmbedUrl"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            title="Vimeo video"></iframe>

          <video
            v-else-if="nativeVideoUrl"
            ref="nativeVideoRef"
            class="media-player video-player"
            :src="nativeVideoUrl"
            :poster="posterImageUrl"
            :controls="videoStarted"
            playsinline
            preload="metadata"
            @play="onNativePlay" />
        </div>

        <div
          v-else-if="imageUrl"
          class="media-container image-container">
          <img
            class="media-player image-player"
            :src="imageUrl"
            :alt="data.image?.alt || ''" />
        </div>

        <figcaption
          v-if="data.caption"
          class="media-caption"
          v-html="data.caption"></figcaption>
      </figure>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const nativeVideoRef = ref(null)
const videoStarted = ref(false)

const enabled = (value) => value === true || value === 1 || value === '1'

const isVideo = computed(() => enabled(props.data?.media_type))
const isVimeo = computed(() => isVideo.value && enabled(props.data?.vimeo))

const nativeVideoUrl = computed(() => props.data?.video?.url || '')
const imageUrl = computed(() => props.data?.image?.url || '')
const posterImageUrl = computed(
  () =>
    props.data?.video_poster?.url ||
    props.data?.poster_image?.url ||
    props.data?.image?.url ||
    ''
)
const posterImageAlt = computed(
  () =>
    props.data?.video_poster?.alt ||
    props.data?.poster_image?.alt ||
    props.data?.image?.alt ||
    'Video poster'
)

const getVimeoId = (url) => {
  if (!url) return ''

  const cleaned = String(url).trim()
  const idMatch = cleaned.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/)

  if (idMatch?.[1]) return idMatch[1]
  if (/^\d+$/.test(cleaned)) return cleaned

  return ''
}

const vimeoEmbedUrl = computed(() => {
  if (!isVimeo.value) return ''

  const vimeoId = getVimeoId(props.data?.vimeo_url)
  if (!vimeoId) return ''

  const controls = videoStarted.value ? 1 : 0
  const autoplay = videoStarted.value ? 1 : 0

  return `https://player.vimeo.com/video/${vimeoId}?controls=${controls}&autoplay=${autoplay}&muted=0&playsinline=1&title=0&byline=0&portrait=0&dnt=1`
})

const hasVideo = computed(() =>
  isVimeo.value ? Boolean(vimeoEmbedUrl.value) : Boolean(nativeVideoUrl.value)
)

const onNativePlay = () => {
  videoStarted.value = true
}

const startPlayback = () => {
  if (videoStarted.value) return

  if (isVimeo.value) {
    videoStarted.value = true
    return
  }

  nativeVideoRef.value?.play().catch(() => {
    // The overlay remains available if the browser blocks playback.
  })
}
</script>

<style lang="scss" scoped>
.article-media-module-component {
  @apply relative bg-white pt-8 lg:py-3;

  .media-content {
    @apply m-0;

    &.small {
      @apply lg:px-[80px];
    }

    .media-container {
      @apply relative overflow-hidden rounded-[12px] lg:rounded-[27px];

      .media-player {
        @apply relative block w-full rounded-[12px] lg:rounded-[27px];
      }

      .image-player {
        @apply h-auto object-cover;
      }

      &.video-container {
        .overlay-fade-enter-active,
        .overlay-fade-leave-active {
          transition: opacity 0.45s ease;
        }

        .overlay-fade-enter-from,
        .overlay-fade-leave-to {
          opacity: 0;
        }

        .play-overlay {
          @apply absolute inset-0 z-20 m-0 cursor-pointer appearance-none border-0 bg-transparent p-0;

          .overlay-poster {
            @apply absolute inset-0 h-full w-full object-cover object-center;
          }

          .play-icon {
            @apply absolute top-1/2 left-1/2 z-10 h-[80px] w-[80px] -translate-x-1/2 -translate-y-1/2 lg:h-[157px] lg:w-[157px];
          }
        }

        iframe.video-player {
          aspect-ratio: 16 / 9;
          height: auto;
        }
      }
    }

    .media-caption {
      @apply relative mt-1 text-[13px] leading-[1.2] text-black lg:mt-7;
    }
  }
}
</style>
